import type { VideoDto } from './videoDto';

/** Section d'une chaîne YouTube à parcourir. */
export type ChannelSection = 'videos' | 'streams';

/**
 * Interroge `GET /api/channel` pour lister les vidéos/streams d'une chaîne.
 *
 * @param channel - Handle (`@nom`) ou URL de la chaîne à parcourir.
 * @param section - Section à parcourir.
 * @param keywords - Mots-clés séparés par des virgules (optionnel).
 * @returns La liste des vidéos correspondantes.
 * @throws {Error} Si la requête échoue ou si le serveur répond une erreur.
 */
export async function searchChannel(
	channel: string,
	section: ChannelSection,
	keywords: string,
): Promise<VideoDto[]> {
	const params = new URLSearchParams({ channel, section });
	if (keywords.trim().length > 0) {
		params.set('keywords', keywords.trim());
	}

	let response: Response;
	try {
		response = await fetch(`/api/channel?${params.toString()}`);
	} catch (error) {
		throw new Error('Impossible de contacter le serveur.', { cause: error });
	}

	if (!response.ok) {
		let message = `Échec de la recherche (${response.status}).`;
		try {
			const body = (await response.json()) as { error?: string };
			message = body.error ?? message;
		} catch {
			// Réponse d'erreur sans JSON exploitable : on garde le message par défaut.
		}
		throw new Error(message);
	}

	try {
		const body = (await response.json()) as { videos: VideoDto[] };
		return body.videos;
	} catch (error) {
		throw new Error('Réponse du serveur invalide.', { cause: error });
	}
}
